import { makeAutoObservable, runInAction } from 'mobx'
import agent from '../api/agent'
import { FirearmModel, FirearmModelFormValues } from '../models/firearmModel'

export default class FirearmModelStore {
  modelRegistry = new Map<string, FirearmModel>()
  selectedModel: FirearmModel | undefined = undefined
  loading = false
  loadingInitial = false
  filterMake = ''
  filterPlatform = ''
  filterCaliber = ''
  filterSortBy = ''
  filterTagIds: string[] = []
  filterTagMatch = 'any'
  pageNumber = 1
  pageSize = 20
  totalCount = 0
  /** Set when list or details fail (e.g. must be logged in). */
  viewError: string | null = null

  constructor () {
    makeAutoObservable(this)
  }

  get modelsByDate () {
    return Array.from(this.modelRegistry.values()).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
  }

  setFilters = (make?: string, platform?: string, caliber?: string, tagIds?: string[], tagMatch?: string, sortBy?: string) => {
    this.filterMake = make ?? ''
    this.filterPlatform = platform ?? ''
    this.filterCaliber = caliber ?? ''
    this.filterSortBy = sortBy ?? ''
    this.filterTagIds = tagIds ?? []
    this.filterTagMatch = tagMatch ?? 'any'
    this.pageNumber = 1
  }

  loadModels = async (append = false) => {
    this.viewError = null
    if (append) this.loading = true
    else this.loadingInitial = true
    try {
      const result = await agent.FirearmModels.list({
        make: this.filterMake || undefined,
        platform: this.filterPlatform || undefined,
        caliber: this.filterCaliber || undefined,
        sortBy: this.filterSortBy || undefined,
        tagIds: this.filterTagIds.length ? this.filterTagIds : undefined,
        tagMatch: this.filterTagMatch,
        pageNumber: this.pageNumber,
        pageSize: this.pageSize
      })
      runInAction(() => {
        if (!append) this.modelRegistry.clear()
        result.items.forEach((m) => this.modelRegistry.set(m.id, m))
        this.totalCount = result.totalCount
        this.loadingInitial = false
        this.loading = false
      })
    } catch (error: unknown) {
      console.error(error)
      const err = error as { response?: { status?: number; data?: { detail?: string } } }
      const message =
        err.response?.status === 401
          ? 'Please log in to view the registry.'
          : err.response?.data?.detail ?? 'Failed to load registry.'
      runInAction(() => {
        this.viewError = message
        this.loadingInitial = false
        this.loading = false
      })
    }
  }

  loadModel = async (id: string) => {
    this.viewError = null
    let model = this.modelRegistry.get(id)
    if (model) {
      this.selectedModel = model
      return model
    }
    this.loadingInitial = true
    try {
      model = await agent.FirearmModels.details(id)
      runInAction(() => {
        this.modelRegistry.set(model!.id, model!)
        this.selectedModel = model
        this.loadingInitial = false
      })
      return model
    } catch (error: unknown) {
      console.error(error)
      const err = error as { response?: { status?: number; data?: { detail?: string } } }
      const message =
        err.response?.status === 401
          ? 'Please log in to view the registry.'
          : err.response?.data?.detail ?? 'Failed to load model.'
      runInAction(() => {
        this.viewError = message
        this.loadingInitial = false
      })
    }
  }

  clearSelectedModel = () => {
    this.selectedModel = undefined
  }

  createModel = async (values: FirearmModelFormValues) => {
    this.loading = true
    try {
      const result = await agent.FirearmModels.create(values)
      runInAction(() => (this.loading = false))
      return result?.id
    } catch (error) {
      console.error(error)
      runInAction(() => (this.loading = false))
    }
  }

  updateModel = async (
    id: string,
    values: Partial<FirearmModelFormValues> & { isVerified?: boolean }
  ) => {
    try {
      await agent.FirearmModels.update(id, values)
      runInAction(() => {
        const m = this.modelRegistry.get(id)
        if (m) {
          const updated = { ...m, ...values }
          this.modelRegistry.set(id, updated)
          if (this.selectedModel?.id === id) this.selectedModel = updated
        }
      })
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  approveModel = async (id: string) => {
    try {
      await agent.FirearmModels.approve(id)
      runInAction(() => {
        const m = this.modelRegistry.get(id)
        if (m) this.modelRegistry.set(id, { ...m, status: 1, isVerified: true })
        if (this.selectedModel?.id === id) { this.selectedModel = { ...this.selectedModel, status: 1, isVerified: true } }
      })
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  rejectModel = async (id: string, reason?: string) => {
    try {
      await agent.FirearmModels.reject(id, reason)
      runInAction(() => {
        const m = this.modelRegistry.get(id)
        if (m) this.modelRegistry.set(id, { ...m, status: 2 })
        if (this.selectedModel?.id === id) this.selectedModel = { ...this.selectedModel, status: 2 }
      })
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  deleteModel = async (id: string) => {
    try {
      await agent.FirearmModels.delete(id)
      runInAction(() => {
        this.modelRegistry.delete(id)
        if (this.selectedModel?.id === id) this.selectedModel = null
      })
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  uploadImage = async (id: string, file: File) => {
    try {
      const url = await agent.FirearmModels.uploadImage(id, file)
      runInAction(() => {
        const m = this.modelRegistry.get(id)
        if (m) this.modelRegistry.set(id, { ...m, coverImageUrl: url })
        if (this.selectedModel?.id === id) this.selectedModel = { ...this.selectedModel, coverImageUrl: url }
      })
      return url
    } catch (error) {
      console.error(error)
      throw error
    }
  }
}
